// src/components/Layout.js
import React, { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import {
  LayoutDashboard, QrCode, History, Settings,
  CreditCard, LogOut, Menu, ChevronRight, ShieldCheck, ArrowLeft,
} from 'lucide-react';

const NAV_ITEMS = [
  { path: '/dashboard', icon: LayoutDashboard, label: 'Dashboard' },
  { path: '/tables',    icon: QrCode,          label: 'Tables & QR Codes' },
  { path: '/sessions',  icon: History,         label: 'Session History' },
  { path: '/billing',   icon: CreditCard,      label: 'Billing' },
  { path: '/settings',  icon: Settings,        label: 'Settings' },
];

export default function Layout({ children }) {
  const { user, merchant, isAdmin, adminMerchant, setAdminMerchant, signOut } = useAuth();
  const navigate  = useNavigate();
  const location  = useLocation();
  const [mobileOpen, setMobileOpen] = useState(false);

  const handleSignOut = async () => {
    await signOut();
    navigate('/signin');
  };

  const handleBackToAdmin = () => {
    setAdminMerchant(null);
    navigate('/admin');
  };

  const SidebarContent = () => (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>

      {/* Logo */}
      <div style={{ padding: '28px 24px 24px', borderBottom: '1px solid rgba(0,0,0,0.06)' }}>
        <div style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: '20px', fontWeight: 600, letterSpacing: '0.12em', color: '#1a1a1a', textTransform: 'uppercase' }}>
          Checks Intel
        </div>
        <div style={{ fontSize: '11px', color: '#999', marginTop: '2px', letterSpacing: '0.05em' }}>Partner Platform</div>
      </div>

      {/* Identity block */}
      <div style={{ padding: '20px 24px', borderBottom: '1px solid rgba(0,0,0,0.06)' }}>
        {isAdmin && !adminMerchant ? (
          // Pure admin view
          <>
            <div style={{ width: '36px', height: '36px', background: 'rgba(64,0,228,0.1)', borderRadius: '10px', display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: '10px' }}>
              <ShieldCheck size={18} color="#4000E4" />
            </div>
            <div style={{ fontSize: '13px', fontWeight: 600, color: '#1a1a1a', marginBottom: '2px' }}>Admin</div>
            <div style={{ fontSize: '11px', color: '#aaa' }}>{user?.email}</div>
            <div style={{ display: 'inline-block', marginTop: '8px', padding: '2px 10px', background: 'rgba(64,0,228,0.1)', borderRadius: '20px', fontSize: '10px', fontWeight: 600, letterSpacing: '0.08em', textTransform: 'uppercase', color: '#4000E4' }}>
              Admin Access
            </div>
          </>
        ) : (
          // Merchant view (real or impersonated)
          <>
            <div style={{ width: '36px', height: '36px', background: 'rgba(64,0,228,0.1)', borderRadius: '10px', display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: '10px', fontSize: '16px' }}>
              🍽
            </div>
            <div style={{ fontSize: '13px', fontWeight: 600, color: '#1a1a1a', marginBottom: '2px' }}>
              {merchant?.name || 'Your Restaurant'}
            </div>
            <div style={{ fontSize: '11px', color: '#aaa' }}>{user?.email}</div>
            <div style={{ display: 'inline-block', marginTop: '8px', padding: '2px 10px', background: 'rgba(0,0,0,0.05)', borderRadius: '20px', fontSize: '10px', fontWeight: 600, letterSpacing: '0.08em', textTransform: 'uppercase', color: '#999' }}>
              {merchant?.plan || 'silver'}
            </div>
          </>
        )}
      </div>

      {/* Nav */}
      <nav style={{ flex: 1, padding: '16px 12px', overflowY: 'auto' }}>
        {NAV_ITEMS.map(({ path, icon: Icon, label }) => {
          const active = location.pathname === path;
          return (
            <button
              key={path}
              onClick={() => { navigate(path); setMobileOpen(false); }}
              style={{
                width: '100%', display: 'flex', alignItems: 'center', gap: '12px',
                padding: '10px 12px', borderRadius: '10px', border: 'none',
                background: active ? 'rgba(64,0,228,0.08)' : 'transparent',
                color: active ? '#4000E4' : '#666',
                fontFamily: "'Outfit', sans-serif", fontSize: '13px',
                fontWeight: active ? 600 : 400,
                cursor: 'pointer', textAlign: 'left', marginBottom: '2px', transition: 'all 0.15s',
              }}
              onMouseEnter={e => { if (!active) e.currentTarget.style.background = 'rgba(0,0,0,0.04)'; }}
              onMouseLeave={e => { if (!active) e.currentTarget.style.background = 'transparent'; }}
            >
              <Icon size={16} />
              {label}
              {active && <ChevronRight size={14} style={{ marginLeft: 'auto', opacity: 0.5 }} />}
            </button>
          );
        })}
      </nav>

      {/* Bottom actions */}
      <div style={{ padding: '16px 12px', borderTop: '1px solid rgba(0,0,0,0.06)', display: 'flex', flexDirection: 'column', gap: '4px' }}>
        {/* Back to Admin — only shown when impersonating */}
        {isAdmin && adminMerchant && (
          <button
            onClick={handleBackToAdmin}
            style={{
              width: '100%', display: 'flex', alignItems: 'center', gap: '12px',
              padding: '10px 12px', borderRadius: '10px', border: 'none',
              background: 'rgba(64,0,228,0.06)', color: '#4000E4',
              fontFamily: "'Outfit', sans-serif", fontSize: '13px', fontWeight: 600,
              cursor: 'pointer', textAlign: 'left', transition: 'all 0.15s',
            }}
            onMouseEnter={e => e.currentTarget.style.background = 'rgba(64,0,228,0.12)'}
            onMouseLeave={e => e.currentTarget.style.background = 'rgba(64,0,228,0.06)'}
          >
            <ArrowLeft size={16} />
            Back to Admin
          </button>
        )}
        <button
          onClick={handleSignOut}
          style={{
            width: '100%', display: 'flex', alignItems: 'center', gap: '12px',
            padding: '10px 12px', borderRadius: '10px', border: 'none',
            background: 'transparent', color: '#999',
            fontFamily: "'Outfit', sans-serif", fontSize: '13px',
            cursor: 'pointer', textAlign: 'left', transition: 'all 0.15s',
          }}
          onMouseEnter={e => { e.currentTarget.style.background = '#fef2f2'; e.currentTarget.style.color = '#dc2626'; }}
          onMouseLeave={e => { e.currentTarget.style.background = 'transparent'; e.currentTarget.style.color = '#999'; }}
        >
          <LogOut size={16} />
          Sign Out
        </button>
      </div>
    </div>
  );

  return (
    <div style={{ display: 'flex', minHeight: '100vh', background: '#F7F4EF', fontFamily: "'Outfit', sans-serif" }}>

      <aside style={{ width: '240px', flexShrink: 0, background: '#fff', borderRight: '1px solid rgba(0,0,0,0.06)', position: 'fixed', top: 0, left: 0, bottom: 0, zIndex: 40, display: 'flex', flexDirection: 'column' }} className="desktop-sidebar">
        <SidebarContent />
      </aside>

      {mobileOpen && (
        <div onClick={() => setMobileOpen(false)} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)', zIndex: 49 }} />
      )}

      <aside style={{ width: '240px', background: '#fff', position: 'fixed', top: 0, left: 0, bottom: 0, zIndex: 50, transform: mobileOpen ? 'translateX(0)' : 'translateX(-100%)', transition: 'transform 0.25s ease', display: 'flex', flexDirection: 'column' }}>
        <SidebarContent />
      </aside>

      <main style={{ flex: 1, marginLeft: '240px', minHeight: '100vh' }} className="main-content">
        <div style={{ display: 'none', padding: '16px 20px', background: '#fff', borderBottom: '1px solid rgba(0,0,0,0.06)', alignItems: 'center', gap: '12px', position: 'sticky', top: 0, zIndex: 30 }} className="mobile-topbar">
          <button onClick={() => setMobileOpen(true)} style={{ background: 'none', border: 'none', cursor: 'pointer', padding: '4px', color: '#555' }}>
            <Menu size={22} />
          </button>
          <span style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: '18px', fontWeight: 600, letterSpacing: '0.1em' }}>Checks Intel</span>
        </div>

        {/* Admin impersonation banner */}
        {isAdmin && adminMerchant && (
          <div style={{ background: '#4000E4', padding: '10px 32px', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <div style={{ fontSize: '13px', color: 'rgba(255,255,255,0.85)' }}>
              Viewing as <strong style={{ color: '#fff' }}>{adminMerchant.name}</strong>
            </div>
            <button
              onClick={handleBackToAdmin}
              style={{ fontSize: '12px', fontWeight: 600, color: '#fff', background: 'rgba(255,255,255,0.15)', border: 'none', borderRadius: '8px', padding: '5px 14px', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '6px' }}
            >
              <ArrowLeft size={13} /> Back to Admin
            </button>
          </div>
        )}

        <div style={{ padding: '32px' }}>
          {children}
        </div>
      </main>

      <style>{`
        @media (max-width: 768px) {
          .desktop-sidebar { display: none !important; }
          .main-content { margin-left: 0 !important; }
          .mobile-topbar { display: flex !important; }
        }
      `}</style>
    </div>
  );
}